/**
 * useTourTTS — Unified text-to-speech hook for the guided-tour system.
 *
 * Replaces the legacy Google TTS on-demand hook and the legacy AI coach
 * Kokoro TTS hook. This hook:
 *
 * - Plays pre-generated audio (`audio_url`) instantly with zero delay
 * - Never calls real-time TTS — steps without pre-generated audio
 *   (coachAudioWorker manifest entries) simply play no audio
 * - Prefetches the next step's audio while the current step plays
 * - Persists mute state in `ta_coach_tts_enabled` localStorage (shared with
 *   the AI coach during migration so the toggle stays in sync)
 * - Stops audio when the tour is minimized or closed (via `canPlayRef`)
 * - Caches blob URLs per step ID for instant replay on Back navigation
 *
 * @created September 2026 — Phase 3 of the tour consolidation.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

const TTS_ENABLED_KEY = 'ta_coach_tts_enabled';

export interface UseTourTTSOptions {
  /** Current step ID (used for blob cache keying and auto-play dedup). */
  currentStepId?: string | null;
  /** Narration text for the current step (title + summary or scripted_description). */
  currentNarrationText: string;
  /** Pre-generated audio URL for the current step (from cloud_storage). */
  currentAudioUrl?: string | null;
  /**
   * Pre-generated spoken title for the current step (from cloud_storage).
   * When present it plays ahead of `currentAudioUrl` as a short spoken
   * step introduction.
   */
  currentTitleAudioUrl?: string | null;
  /** Next step ID (for prefetch dedup). */
  nextStepId?: string | null;
  /** Pre-generated audio URL for the next step (prefetched in background). */
  nextAudioUrl?: string | null;
  /** Spoken title URL for the next step (prefetched in background). */
  nextTitleAudioUrl?: string | null;
  /** Kokoro voice override — retained for API compatibility with callers. */
  voice?: string;
  /** Whether the tour is open (audio stops when false). */
  isOpen: boolean;
  /** Whether the tour is minimized (audio stops when true). */
  isMinimized: boolean;
  /** Auto-play audio when the step changes (from schema.audio.auto_play). */
  autoPlay?: boolean;
  /** Visibility gate ref — when `current === false`, playback is suppressed. */
  canPlayRef?: MutableRefObject<boolean>;
}

export interface UseTourTTSResult {
  /** Whether TTS is enabled (not muted) by the user. */
  enabled: boolean;
  /** Toggle TTS on/off. */
  toggleEnabled: () => void;
  /** Set TTS enabled state directly. */
  setEnabled: (v: boolean) => void;
  /** Whether audio is currently playing. */
  isPlaying: boolean;
  /** Whether audio is being fetched. */
  isLoading: boolean;
  /** Whether audio is paused (can be resumed). */
  isPaused: boolean;
  /** Play the current step's audio (pre-generated or on-demand synthesis). */
  play: () => void;
  /** Stop any currently playing audio. */
  stop: () => void;
  /** Pause the currently playing audio (can be resumed). */
  pause: () => void;
  /** Resume paused audio. */
  resume: () => void;
  /** Play audio from a remote URL (fetches → blob → plays). Returns blob URL. */
  playUrl: (url: string) => Promise<string | null>;
  /** Pre-fetch audio from a remote URL without playing. Returns blob URL. */
  fetchUrl: (url: string) => Promise<string | null>;
  /** Play a previously cached blob URL. */
  playBlobUrl: (blobUrl: string) => void;
  /** The live audio element. */
  audioRef: MutableRefObject<HTMLAudioElement | null>;
}

export function useTourTTS(options: UseTourTTSOptions): UseTourTTSResult {
  const {
    currentStepId,
    currentNarrationText,
    currentAudioUrl,
    currentTitleAudioUrl,
    nextStepId,
    nextAudioUrl,
    nextTitleAudioUrl,
    isOpen,
    isMinimized,
    autoPlay = false,
    canPlayRef,
  } = options;

  const [enabled, setEnabledState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    // Default ON — audio is a core tour feature. Users who explicitly muted
    // keep their preference; everyone else hears narration out of the box.
    const stored = localStorage.getItem(TTS_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentBlobUrlRef = useRef<string | null>(null);
  // Blob cache keyed by step ID — allows instant replay on Back navigation
  // and lets prefetched audio play instantly when the step becomes current.
  const blobCacheRef = useRef<Record<string, string>>({});
  // Track which step is currently playing/loading to avoid duplicate work.
  const playingStepIdRef = useRef<string | null>(null);
  const loadingStepIdRef = useRef<string | null>(null);
  // Track whether a prefetch is in progress for the next step.
  const prefetchStepIdRef = useRef<string | null>(null);
  // Mirror of isPaused for use inside effects (avoids re-running the
  // auto-play effect when the user pauses mid-step).
  const isPausedRef = useRef(false);
  // Once the user pauses, suppress auto-play on subsequent step changes
  // until they explicitly press play again.
  const suppressAutoplayRef = useRef(false);

  // ── Helpers ────────────────────────────────────────────────────────────

  // Mirror isOpen/isMinimized in a ref so async fetch→play chains can check
  // visibility at play time — otherwise audio started just before the tour
  // minimized (or edit mode opened) would begin playing anyway when the
  // fetch resolves.
  const gateRef = useRef(isOpen && !isMinimized);
  useEffect(() => {
    gateRef.current = isOpen && !isMinimized;
  }, [isOpen, isMinimized]);

  const canPlayNow = useCallback(() => {
    if (canPlayRef && canPlayRef.current === false) return false;
    if (!gateRef.current) return false;
    return true;
  }, [canPlayRef]);

  const revokeBlobUrl = useCallback((blobUrl: string | null) => {
    if (!blobUrl) return;
    // Don't revoke if it's still in the cache (may be needed for replay).
    const inCache = Object.values(blobCacheRef.current).includes(blobUrl);
    if (!inCache) {
      URL.revokeObjectURL(blobUrl);
    }
  }, []);

  const stopInternal = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // Revoke the current blob URL only if it's not cached for replay.
    if (currentBlobUrlRef.current) {
      revokeBlobUrl(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
    setIsPaused(false);
    isPausedRef.current = false;
    playingStepIdRef.current = null;
    loadingStepIdRef.current = null;
  }, [revokeBlobUrl]);

  // ── Mute toggle ────────────────────────────────────────────────────────

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
    try {
      localStorage.setItem(TTS_ENABLED_KEY, value.toString());
    } catch {
      // Ignore quota errors
    }
    if (!value) {
      stopInternal();
    } else {
      // Unmuting expresses intent to hear audio — clear pause suppression.
      suppressAutoplayRef.current = false;
    }
  }, [stopInternal]);

  const toggleEnabled = useCallback(() => {
    setEnabled(!enabled);
  }, [enabled, setEnabled]);

  const stop = useCallback(() => {
    stopInternal();
  }, [stopInternal]);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPaused(true);
      isPausedRef.current = true;
      // Keep isPlaying true — audio is loaded, just not advancing
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch((err) => {
        console.warn('[useTourTTS] Resume playback blocked:', err);
      });
      setIsPaused(false);
      isPausedRef.current = false;
    }
  }, []);

  // ── Playback primitives ────────────────────────────────────────────────

  const fetchUrl = useCallback(async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, []);

  const playBlobUrl = useCallback((blobUrl: string) => {
    if (!enabled) return;
    stopInternal();

    currentBlobUrlRef.current = blobUrl;
    const audio = new Audio(blobUrl);
    audioRef.current = audio;

    setIsPlaying(true);
    setIsLoading(false);
    setIsPaused(false);

    audio.onended = () => {
      setIsPlaying(false);
      audioRef.current = null;
      // Clear the playing-step marker so play() can replay this step
      // (otherwise it would be treated as a stop-toggle).
      playingStepIdRef.current = null;
      // Don't revoke — blob may be cached for replay
    };
    audio.onerror = () => {
      setIsPlaying(false);
      setIsLoading(false);
      audioRef.current = null;
    };

    if (!canPlayNow()) {
      stopInternal();
      return;
    }

    audio.play().catch((err) => {
      console.warn('[useTourTTS] Autoplay blocked:', err);
      setIsPlaying(false);
    });
  }, [enabled, stopInternal, canPlayNow]);

  /**
   * Play several blob URLs back-to-back as one logical narration — used for
   * the [spoken title] → [scripted description] chain. Each segment plays
   * to completion before the next begins; stop/pause state applies to
   * whichever segment is currently sounding.
   */
  const playBlobUrlSequence = useCallback((blobUrls: string[]) => {
    if (!enabled || blobUrls.length === 0) return;
    stopInternal();
    if (!canPlayNow()) return;

    const playAt = (index: number) => {
      const blobUrl = blobUrls[index];
      currentBlobUrlRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;

      setIsPlaying(true);
      setIsLoading(false);
      setIsPaused(false);

      audio.onended = () => {
        audioRef.current = null;
        if (index + 1 < blobUrls.length) {
          playAt(index + 1);
        } else {
          setIsPlaying(false);
          // Clear the playing-step marker so play() can replay this step
          // (otherwise it would be treated as a stop-toggle).
          playingStepIdRef.current = null;
        }
      };
      audio.onerror = () => {
        setIsPlaying(false);
        setIsLoading(false);
        audioRef.current = null;
      };

      audio.play().catch((err) => {
        console.warn('[useTourTTS] Autoplay blocked:', err);
        setIsPlaying(false);
      });
    };

    playAt(0);
  }, [enabled, stopInternal, canPlayNow]);

  const playUrl = useCallback(async (url: string): Promise<string | null> => {
    if (!enabled) return null;
    stopInternal();
    setIsLoading(true);

    const blobUrl = await fetchUrl(url);
    if (!blobUrl) {
      setIsLoading(false);
      return null;
    }

    if (!canPlayNow()) {
      URL.revokeObjectURL(blobUrl);
      setIsLoading(false);
      return null;
    }

    playBlobUrl(blobUrl);
    return blobUrl;
  }, [enabled, stopInternal, fetchUrl, playBlobUrl, canPlayNow]);

  // ── Play current step (pre-generated audio only — no live synthesis) ──

  const play = useCallback(() => {
    if (!enabled || !currentStepId || !currentNarrationText) return;

    // Manual play clears the pause suppression so later steps auto-play again.
    suppressAutoplayRef.current = false;

    // Already playing this step → stop (toggle behavior)
    if (playingStepIdRef.current === currentStepId) {
      stopInternal();
      return;
    }

    // Already loading this step → don't duplicate
    if (loadingStepIdRef.current === currentStepId) return;

    loadingStepIdRef.current = currentStepId;
    setIsLoading(true);

    (async () => {
      // Ordered segments: spoken title first (when generated), then the
      // scripted description. Each is cached under its own key.
      const segments: Array<{ cacheKey: string; url: string | null | undefined }> = [
        { cacheKey: `${currentStepId}__title`, url: currentTitleAudioUrl },
        { cacheKey: currentStepId, url: currentAudioUrl },
      ];

      const blobUrls: string[] = [];
      for (const seg of segments) {
        let blobUrl: string | null = blobCacheRef.current[seg.cacheKey];
        if (!blobUrl && seg.url) {
          blobUrl = await fetchUrl(seg.url);
          if (blobUrl) {
            blobCacheRef.current[seg.cacheKey] = blobUrl;
          }
        }
        if (blobUrl) blobUrls.push(blobUrl);
      }

      // No pre-generated audio → silence. Real-time TTS is never called.
      if (blobUrls.length === 0) {
        setIsLoading(false);
        loadingStepIdRef.current = null;
        return;
      }

      // Guard: don't play if the tour became invisible during the fetch
      if (!canPlayNow()) {
        setIsLoading(false);
        loadingStepIdRef.current = null;
        return;
      }

      playBlobUrlSequence(blobUrls);
      playingStepIdRef.current = currentStepId;
      loadingStepIdRef.current = null;
    })();
  }, [
    enabled,
    currentStepId,
    currentNarrationText,
    currentAudioUrl,
    currentTitleAudioUrl,
    fetchUrl,
    playBlobUrlSequence,
    canPlayNow,
    stopInternal,
  ]);

  // ── Prefetch next step audio ──────────────────────────────────────────

  useEffect(() => {
    if (!enabled || !nextStepId) return;
    // Already cached or already prefetching
    if (blobCacheRef.current[nextStepId]) return;
    if (prefetchStepIdRef.current === nextStepId) return;

    prefetchStepIdRef.current = nextStepId;

    (async () => {
      // Only prefetch pre-generated audio — no live synthesis fallback.
      const blobUrl = nextAudioUrl ? await fetchUrl(nextAudioUrl) : null;

      if (blobUrl) {
        blobCacheRef.current[nextStepId] = blobUrl;
      }

      const titleBlobUrl = nextTitleAudioUrl ? await fetchUrl(nextTitleAudioUrl) : null;
      if (titleBlobUrl) {
        blobCacheRef.current[`${nextStepId}__title`] = titleBlobUrl;
      }

      // Clear prefetch flag only if it still points to this step
      if (prefetchStepIdRef.current === nextStepId) {
        prefetchStepIdRef.current = null;
      }
    })();
  }, [enabled, nextStepId, nextAudioUrl, nextTitleAudioUrl, fetchUrl]);

  // ── Auto-play when the step changes ───────────────────────────────────

  useEffect(() => {
    if (!autoPlay || !enabled || !isOpen || isMinimized || !currentStepId) return;
    if (playingStepIdRef.current === currentStepId) return;
    if (loadingStepIdRef.current === currentStepId) return;

    // Respect an explicit pause: if the user paused (or has suppressed
    // auto-play by pausing a previous step), drop the stale audio element
    // but don't start the new step's narration until they press play.
    if (isPausedRef.current || suppressAutoplayRef.current) {
      suppressAutoplayRef.current = true;
      stopInternal();
      return;
    }

    play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, enabled, isOpen, isMinimized, currentStepId]);

  // ── Stop when tour closes or minimizes ─────────────────────────────────

  useEffect(() => {
    if (!isOpen || isMinimized) {
      stopInternal();
    }
  }, [isOpen, isMinimized, stopInternal]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Revoke all cached blob URLs
      for (const blobUrl of Object.values(blobCacheRef.current)) {
        URL.revokeObjectURL(blobUrl);
      }
      blobCacheRef.current = {};
      if (currentBlobUrlRef.current) {
        // currentBlobUrl may be in the cache (already revoked above) or not
        const inCache = Object.values({}).includes(currentBlobUrlRef.current);
        if (!inCache) {
          URL.revokeObjectURL(currentBlobUrlRef.current);
        }
        currentBlobUrlRef.current = null;
      }
    };
  }, []);

  return {
    enabled,
    toggleEnabled,
    setEnabled,
    isPlaying,
    isLoading,
    isPaused,
    play,
    stop,
    pause,
    resume,
    playUrl,
    fetchUrl,
    playBlobUrl,
    audioRef,
  };
}
